import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../config/firebase";

const C = {
  bg:     "#F8F6F1",
  header: "#2D5A3D",
  text:   "#1C1C1E",
  muted:  "#8A8A8E",
  border: "#D3D1C7",
  terra:  "#C4622D",
};

function fmt(n) {
  return Number(n || 0).toFixed(2).replace(".", ",") + " €";
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR");
}

export default function CertificatPublic() {
  const { certId } = useParams();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "certificats", certId));
        if (snap.exists()) {
          setData(snap.data());
        } else {
          setError("Certificat introuvable.");
        }
      } catch {
        setError("Impossible de charger le certificat.");
      } finally {
        setLoading(false);
      }
    })();
  }, [certId]);

  if (loading) return (
    <div style={{
      minHeight: "100vh", background: C.bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "-apple-system, 'SF Pro Display', sans-serif",
    }}>
      <div style={{ textAlign: "center", color: C.muted }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🚐</div>
        <div>Chargement du certificat…</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{
      minHeight: "100vh", background: C.bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "-apple-system, 'SF Pro Display', sans-serif",
    }}>
      <div style={{ textAlign: "center", color: C.muted, padding: 24 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontSize: 15, fontWeight: 600 }}>{error}</div>
      </div>
    </div>
  );

  const { vanNom, vanImmat, certDisplayId, updatedAt, interventionsList = [], factures_garage = [] } = data;

  return (
    <div style={{
      maxWidth: 480, margin: "0 auto", minHeight: "100vh",
      background: C.bg, paddingBottom: 40,
      fontFamily: "-apple-system, 'SF Pro Display', 'Segoe UI', sans-serif",
    }}>

      {/* Header */}
      <div style={{ background: C.header, padding: "28px 24px 24px", textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>🚐</div>
        <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(248,246,241,0.6)", letterSpacing: 2, marginBottom: 4 }}>
          CHECKVAN
        </div>
        <div style={{ fontSize: 20, fontWeight: 900, color: "#F8F6F1" }}>
          Certificat d'entretien
        </div>
      </div>

      <div style={{ padding: "20px 16px 0" }}>

        {/* Identité du van */}
        <div style={{
          background: "white", borderRadius: 14, padding: "18px 16px",
          marginBottom: 12, border: `1px solid ${C.border}`,
          boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 1, marginBottom: 10 }}>
            VÉHICULE
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.text }}>{vanNom || "—"}</div>
          <div style={{ fontSize: 14, color: C.muted, marginTop: 4, fontWeight: 600, letterSpacing: 1 }}>
            {vanImmat || "—"}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
            Camping-car / Van
          </div>
        </div>

        {/* Tableau interventions */}
        <div style={{
          background: "white", borderRadius: 14,
          marginBottom: 12, border: `1px solid ${C.border}`,
          boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}>
          {/* En-tête */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "80px 70px 1fr 80px",
            background: C.header,
            padding: "10px 16px",
            gap: 8,
          }}>
            {["DATE", "KM", "DESCRIPTION", "MONTANT"].map(col => (
              <div key={col} style={{
                fontSize: 9, fontWeight: 700, color: "rgba(248,246,241,0.85)",
                letterSpacing: 0.8,
                textAlign: col === "MONTANT" ? "right" : "left",
              }}>
                {col}
              </div>
            ))}
          </div>

          {interventionsList.length === 0 ? (
            <div style={{ padding: "20px 16px", textAlign: "center", color: C.muted, fontSize: 13 }}>
              Aucune intervention enregistrée
            </div>
          ) : (
            interventionsList.map((item, i) => (
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "80px 70px 1fr 80px",
                  padding: "12px 16px",
                  gap: 8,
                  background: i % 2 === 0 ? "white" : C.bg,
                  borderTop: `1px solid ${C.border}`,
                  alignItems: "start",
                }}
              >
                <div style={{ fontSize: 12, color: C.muted }}>{fmtDate(item.date)}</div>
                <div style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>
                  {item.km ? Number(item.km).toLocaleString("fr-FR") : "—"}
                </div>
                <div style={{ fontSize: 12, color: C.text, fontWeight: 600, wordBreak: "break-word" }}>
                  {item.description || "—"}
                </div>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.terra, textAlign: "right" }}>
                  {item.cout > 0 ? fmt(item.cout) : "—"}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Factures Garage */}
        {factures_garage.length > 0 && (
          <div style={{
            background: "white", borderRadius: 14, padding: "16px",
            marginBottom: 12, border: `1px solid ${C.border}`,
            boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 1, marginBottom: 12 }}>
              FACTURES GARAGE ({factures_garage.length})
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {factures_garage.map((f, i) => (
                <div key={f.factureId || i} style={{ borderRadius: 10, overflow: "hidden", position: "relative" }}>
                  <img
                    src={f.image}
                    alt="Facture"
                    style={{ width: "100%", aspectRatio: "4/3", objectFit: "cover", display: "block" }}
                  />
                  {f.date && (
                    <div style={{
                      position: "absolute", bottom: 0, left: 0, right: 0,
                      background: "linear-gradient(transparent, rgba(0,0,0,0.65))",
                      padding: "16px 8px 6px",
                      fontSize: 10, color: "white", fontWeight: 700,
                    }}>
                      {new Date(f.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cachet */}
        <div style={{
          background: "#EAF3DE", borderRadius: 14, padding: "16px",
          border: "1.5px solid #5DCAA5", textAlign: "center",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.header, letterSpacing: 1, marginBottom: 6 }}>
            ✓ CERTIFIÉ CHECKVAN
          </div>
          <div style={{ fontSize: 13, fontWeight: 900, color: C.header, letterSpacing: 2, marginBottom: 6 }}>
            {certDisplayId || certId}
          </div>
          <div style={{ fontSize: 10, color: "#085041", opacity: 0.7 }}>
            Horodatage : {fmtDate(updatedAt)} · CheckVan Carnet de bord numérique
          </div>
        </div>

      </div>
    </div>
  );
}
